import { forwardRef, isValidElement, type ComponentProps, type ForwardedRef, type ReactNode } from 'react'
import { cva, VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { ArrowUpRight, Loader2 } from 'lucide-react'
import { useGroupedButtonProps } from '../button-group'
import { keysOf } from '../../lib/keys-of'
import { HotkeyChips } from './nested/hotkey-chips'

/**
 * How much emphasis the button carries. What it does is `tone`, and the two are separate so a
 * destructive action can sit quietly in a menu row or loudly in a dialog without changing
 * component.
 *
 * Disabled drops the colour and keeps the shape: a filled variant becomes a flat wash, outline keeps
 * a quiet edge, ghost stays bare, and the label greys on all four. Primary and secondary share the
 * wash, so what still tells two disabled buttons apart is fill against edge against nothing. Keyed
 * to `data-disabled` rather than `:disabled`, so a loading button, which is natively disabled to
 * block a second press, keeps its fill, and a disabled link, which has no
 * `:disabled`, still gets one. The disabled fill also beats every `hover:` fill: both are one class and
 * one state, and Tailwind emits `data-*` variants after `hover`. So a disabled button never lights
 * up under the pointer, and it keeps pointer events for its not-allowed cursor and its tooltip.
 */
const VARIANTS = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover data-[disabled]:bg-element-disabled',
  secondary: 'bg-element text-foreground hover:bg-element-hover data-[disabled]:bg-element-disabled',
  // Carries its edge instead of a ground, for a second action on a surface that already has a fill
  // behind it. The border is the system hairline, so the label carries the contrast.
  outline: [
    'border border-strong bg-transparent text-foreground hover:bg-element-hover',
    'data-[disabled]:border-disabled data-[disabled]:bg-transparent',
  ].join(' '),
  // No ground until it is touched, for toolbars and rows where a fill per item would be noise.
  ghost: 'bg-transparent text-foreground hover:bg-element-hover data-[disabled]:bg-transparent',
}

/**
 * What the button does. Fills and text colours live in `compoundVariants`, because they depend on the
 * variant too. What a tone owns alone is its focus ring (and a fixed background's disabled text), so a
 * toned action keeps its colour all the way through.
 */
const TONES = {
  neutral: '',
  danger: 'focus-visible:focus-ring-danger',
  success: 'focus-visible:focus-ring-success',
  // Fixed colours for a background the theme does not own: a photo, a brand fill, an always-dark panel.
  // Named for the on-light and on-dark tokens they are built from: the black and white alpha ramps,
  // so they never swap with the theme and the translucent steps pick up whatever sits behind.
  // Disabled text stays on the background's own ramp (step 8, as --foreground-disabled is gray-8), since
  // the theme's disabled token would flip under a background that does not.
  'on-light': 'focus-visible:focus-ring-on-light data-[disabled]:text-black-8',
  'on-dark': 'focus-visible:focus-ring-on-dark data-[disabled]:text-white-8',
  // The theme's own contrast colour: `on-light` in light, `on-dark` in dark. For a black call to action
  // on the theme's ground, which `on-light` alone would sink into a dark panel.
  inverse:
    'focus-visible:focus-ring-on-light dark:focus-visible:focus-ring-on-dark data-[disabled]:text-black-8 dark:data-[disabled]:text-white-8',
}

/**
 * Six heights and three type steps, from the type scale: text-xs 12, text-sm 13, text-md 15.
 * 13px does most of the work: 24, 28, 32 and 40 all take it. The
 * first three appear together in a toolbar or a form footer and share a label for that reason; 40
 * joins them because there the height and the fill already carry the emphasis, and a label that
 * stepped up as well would read as a landing page's button. 12 is only at 20, where an 18px line
 * box does not fit, and 15 is left to 48.
 *
 * Icons step with the text, not with the box, so a 40px button carries a 14px glyph sized to the
 * label beside it rather than to the height it sits in.
 */
const SIZES = {
  xs: '[--btn-h:20px] [--btn-pad:6px] gap-1 text-xs [&_svg:not([class*="size-"])]:size-3 rounded-[var(--radius-size-xs)]',
  sm: '[--btn-h:24px] [--btn-pad:8px] gap-1.5 text-sm [&_svg:not([class*="size-"])]:size-3.5 rounded-[var(--radius-size-sm)]',
  md: '[--btn-h:28px] [--btn-pad:10px] gap-1.5 text-sm [&_svg:not([class*="size-"])]:size-3.5 rounded-[var(--radius-size-md)]',
  lg: '[--btn-h:32px] [--btn-pad:12px] gap-2 text-sm [&_svg:not([class*="size-"])]:size-3.5 rounded-[var(--radius-size-lg)]',
  xl: '[--btn-h:40px] [--btn-pad:16px] gap-2 text-sm [&_svg:not([class*="size-"])]:size-3.5 rounded-[var(--radius-size-xl)]',
  '2xl':
    '[--btn-h:48px] [--btn-pad:20px] gap-2 text-md [&_svg:not([class*="size-"])]:size-4 rounded-[var(--radius-size-2xl)]',
}

const SHAPES = {
  // The radius clamps to half the height, so icon-only gives a circle and a label gives a pill. The
  // squircle comes off: on a clamped radius it replaces the round ends rather than softening them,
  // handing back the rectangle this setting exists to leave.
  rounded: 'rounded-full corner-sharp',
  square: 'aspect-square p-0',
  circle: 'aspect-square rounded-full corner-sharp p-0',
}

export const BUTTON_DEFAULTS = { variant: 'primary', tone: 'neutral', size: 'md' } as const

const buttonVariants = cva(
  [
    'user-select-none flex w-fit cursor-pointer items-center justify-center whitespace-nowrap font-medium',
    // Colour, the focus ring and the press. The press scale is ours, and it has to be in this list or
    // it snaps. No shadow on hover: that costs a paint the press does not need, and the lift reads as
    // the button leaving the surface it is attached to.
    // The hover ground fades both ways instead of snapping on under the pointer. The curve is an
    // arbitrary property because Tailwind drops an arbitrary ease class as ambiguous with the animation plugin.
    'transition-[background-color,color,border-color,box-shadow,transform] duration-150 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
    // The system focus ring, restated as a utility so a caller's shadow stacks with it and a tone can
    // recolour it.
    'focus-visible:focus-ring',
    // The press is the scale and nothing else. A pressed colour on top of it says the same thing
    // twice, and under a pointer the hover ground is already showing, so a third step down only
    // darkens a button that has not changed state.
    'active:scale-[0.96] motion-reduce:active:scale-100 disabled:active:scale-100 aria-disabled:active:scale-100',
    // Under 4.5:1 by design and exempt, which means a disabled button must never be the only place a
    // fact appears.
    'data-[disabled]:cursor-not-allowed data-[disabled]:text-foreground-disabled',
    // A size sets `--btn-h`, `--btn-pad` and its corner; the box, padding and hotkey chips read the
    // first two, so nothing can drift from the height. The corner is half the height, from
    // `--radius-size-*`, squircled so it reads as a softened rectangle rather than a pill.
    'h-[var(--btn-h)] px-[var(--btn-pad)] corner-squircle',
    // Optical padding. Set by `data-icon` so only a real glyph pulls its edge in; see opticalSide.
    'data-[icon=leading]:pl-[calc(var(--btn-pad)-2px)] data-[icon=trailing]:pr-[calc(var(--btn-pad)-2px)]',
    'data-[icon=both]:px-[calc(var(--btn-pad)-2px)]',
  ],
  {
    variants: {
      variant: VARIANTS,
      tone: TONES,
      size: SIZES,
      shape: SHAPES,
      block: {
        true: 'flex w-full min-w-0',
      },
    },

    compoundVariants: [
      // The danger solid carries its own text colour; success takes dark text because its solid is
      // a light step-9 green that white would not clear AA on.
      {
        variant: 'primary',
        tone: 'danger',
        class: 'bg-danger text-on-danger hover:bg-danger-hover',
      },
      {
        variant: 'primary',
        tone: 'success',
        class: 'bg-success text-on-success hover:bg-success-hover',
      },

      // The wash climbs 3 / 4 / 5 exactly as the neutral surfaces do, and step-12 text sits on it.
      {
        variant: 'secondary',
        tone: 'danger',
        class: 'bg-danger-subtle text-danger hover:bg-danger-subtle-hover',
      },
      {
        variant: 'secondary',
        tone: 'success',
        class: 'bg-success-subtle text-success hover:bg-success-subtle-hover',
      },

      {
        variant: 'outline',
        tone: 'danger',
        class: 'border-danger text-danger hover:bg-danger-subtle',
      },
      {
        variant: 'outline',
        tone: 'success',
        class: 'border-success text-success hover:bg-success-subtle',
      },

      {
        variant: 'ghost',
        tone: 'danger',
        class: 'text-danger hover:bg-danger-subtle',
      },
      {
        variant: 'ghost',
        tone: 'success',
        class: 'text-success hover:bg-success-subtle',
      },

      // Fixed backgrounds. Their disabled text is on the tone itself.
      {
        variant: 'primary',
        tone: 'on-light',
        class: 'bg-on-light text-on-light-inverse hover:bg-on-light-hover data-[disabled]:bg-on-light-subtle',
      },
      {
        variant: 'secondary',
        tone: 'on-light',
        class: 'bg-on-light-subtle text-on-light hover:bg-on-light-subtle-hover data-[disabled]:bg-on-light-subtle',
      },
      {
        variant: 'outline',
        tone: 'on-light',
        class: 'border-on-light text-on-light hover:bg-on-light-subtle-hover data-[disabled]:border-on-light',
      },
      {
        variant: 'ghost',
        tone: 'on-light',
        class: 'text-on-light hover:bg-on-light-subtle-hover',
      },

      {
        variant: 'primary',
        tone: 'on-dark',
        class: 'bg-on-dark text-on-dark-inverse hover:bg-on-dark-hover data-[disabled]:bg-on-dark-subtle',
      },
      {
        variant: 'secondary',
        tone: 'on-dark',
        class: 'bg-on-dark-subtle text-on-dark hover:bg-on-dark-subtle-hover data-[disabled]:bg-on-dark-subtle',
      },
      {
        variant: 'outline',
        tone: 'on-dark',
        class: 'border-on-dark text-on-dark hover:bg-on-dark-subtle-hover data-[disabled]:border-on-dark',
      },
      {
        variant: 'ghost',
        tone: 'on-dark',
        class: 'text-on-dark hover:bg-on-dark-subtle-hover',
      },

      {
        variant: 'primary',
        tone: 'inverse',
        class: [
          'bg-on-light text-on-light-inverse hover:bg-on-light-hover data-[disabled]:bg-on-light-subtle',
          'dark:bg-on-dark dark:text-on-dark-inverse dark:hover:bg-on-dark-hover dark:data-[disabled]:bg-on-dark-subtle',
        ],
      },
      {
        variant: 'secondary',
        tone: 'inverse',
        class: [
          'bg-on-light-subtle text-on-light hover:bg-on-light-subtle-hover data-[disabled]:bg-on-light-subtle',
          'dark:bg-on-dark-subtle dark:text-on-dark dark:hover:bg-on-dark-subtle-hover dark:data-[disabled]:bg-on-dark-subtle',
        ],
      },
      {
        variant: 'outline',
        tone: 'inverse',
        class: [
          'border-on-light text-on-light hover:bg-on-light-subtle-hover data-[disabled]:border-on-light',
          'dark:border-on-dark dark:text-on-dark dark:hover:bg-on-dark-subtle-hover dark:data-[disabled]:border-on-dark',
        ],
      },
      {
        variant: 'ghost',
        tone: 'inverse',
        class: 'text-on-light hover:bg-on-light-subtle-hover dark:text-on-dark dark:hover:bg-on-dark-subtle-hover',
      },
    ],

    defaultVariants: BUTTON_DEFAULTS,
  },
)

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']
export type ButtonTone = VariantProps<typeof buttonVariants>['tone']
export type ButtonSize = VariantProps<typeof buttonVariants>['size']
export type ButtonShape = VariantProps<typeof buttonVariants>['shape']

/** Read off the maps the cva styles from, so the design system cannot list an option that is not here. */
export const BUTTON_VARIANTS = keysOf(VARIANTS)
export const BUTTON_TONES = keysOf(TONES)
export const BUTTON_SIZES = keysOf(SIZES)
export const BUTTON_SHAPES = keysOf(SHAPES)

type ButtonProps = Omit<ComponentProps<'button'>, 'type'> &
  Pick<ComponentProps<'a'>, 'href' | 'target' | 'rel' | 'download'> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean
    left?: ReactNode
    right?: ReactNode
    hotkey?: string
    /** `<button>` only; ignored once `href` turns this into an anchor. */
    type?: ComponentProps<'button'>['type']
  }

/**
 * An icon carries more air inside its own box than a letter does, so a glyph at the edge reads
 * further in than a cap does. Pull the side it sits on back by 2px and the two ends look equal.
 * Only for icons: a text or badge slot has no such air, and pulling it back would just look tight.
 */
function opticalSide(left: boolean, right: boolean): 'leading' | 'trailing' | 'both' | undefined {
  if (left && right) return 'both'
  if (left) return 'leading'
  if (right) return 'trailing'
  return undefined
}

/** A wrapper around text is not an icon, so the pull-back keys off the element, not the slot. */
function isIconNode(node: ReactNode): boolean {
  return isValidElement(node) && typeof node.type !== 'string'
}

/**
 * A new tab is a thing that happens to the user, so the button says so before they click. Keyed to
 * `target`, not to the host being external: a link that navigates in place has no surprise to warn
 * about. Dimmed because it is a hint about what will happen, not a second action.
 */
const EXTERNAL_ICON = <ArrowUpRight className="flex-shrink-0 opacity-60" />

// The ref is whichever element rendered, so a caller holding a plain button ref still fits.
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant,
      tone,
      size,
      shape,
      block,
      className,
      children,
      disabled,
      loading,
      left,
      right,
      hotkey,
      href,
      target,
      rel,
      download,
      type,
      ...props
    },
    ref,
  ) => {
    const {
      variant: resolvedVariant,
      tone: resolvedTone,
      size: resolvedSize,
    } = useGroupedButtonProps({ variant, tone, size }, BUTTON_DEFAULTS)

    const isIconButton = shape === 'square' || shape === 'circle'
    const isLink = href !== undefined
    const isDisabled = Boolean(loading || disabled)
    const loader = <Loader2 className="animate-spin" />

    // The external hint fills the trailing slot when nothing else claims it. Resolved before the
    // spinner so a loading link puts the spinner here rather than beside the hint: the two should
    // never co-occur, and if they do the spinner is the one carrying news.
    const trailing = right ?? (isLink && target === '_blank' && !isIconButton ? EXTERNAL_ICON : undefined)

    // The spinner takes the leading slot so the box does not change width under the pointer. A
    // button with only a trailing icon keeps the spinner on that side for the same reason.
    let leftRender = left
    let rightRender = trailing
    if (loading && !isIconButton) {
      if (trailing && !left) {
        rightRender = loader
      } else {
        leftRender = loader
      }
    }

    const classes = twMerge(
      buttonVariants({ variant: resolvedVariant, tone: resolvedTone, size: resolvedSize, shape, block }),
      isLink && 'no-underline',
      // `loading` disables the button, but it is not the `disabled` state. The spinner already says
      // the press landed, so the look an unavailable control gets is wrong here (HIG-6220).
      loading && 'cursor-progress',
      className,
    )

    const shared = {
      'data-slot': isLink ? 'button-link' : 'button',
      'data-variant': resolvedVariant,
      'data-tone': resolvedTone,
      'aria-busy': loading || undefined,
      'data-disabled': (disabled && !loading) || undefined,
      className: classes,
      ...props,
    }

    const body = isIconButton ? (
      loading ? (
        loader
      ) : (
        children
      )
    ) : (
      <>
        {leftRender}
        {children}
        {rightRender}
        {hotkey && <HotkeyChips hotkey={hotkey} size={resolvedSize} />}
      </>
    )

    const dataIcon = isIconButton ? undefined : opticalSide(isIconNode(leftRender), isIconNode(rightRender))

    if (isLink) {
      return (
        <a
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          // `props` is typed for a button, because that is what this component mostly is. The
          // anchor branch takes the same bag; the attributes that do not apply are simply absent
          // at every call site that passes an href.
          {...(shared as ComponentProps<'a'>)}
          data-icon={dataIcon}
          // An anchor has no `disabled`. Dropping the href is what actually stops it: it leaves
          // the tab order and stops being announced as a link, which `pointer-events: none` does
          // neither of. `aria-disabled` is what is left to say so.
          href={isDisabled ? undefined : href}
          aria-disabled={isDisabled || undefined}
          // Without an href there is nothing to follow, but a click handler would still run.
          onClick={isDisabled ? undefined : (shared as ComponentProps<'a'>).onClick}
          target={target}
          // A `_blank` target without this hands the opener a handle on our window.
          rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)}
          download={download}
        >
          {body}
        </a>
      )
    }

    return (
      <button
        ref={ref as ForwardedRef<HTMLButtonElement>}
        {...shared}
        data-icon={dataIcon}
        type={type ?? 'button'}
        disabled={isDisabled}
      >
        {body}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
export type { ButtonProps }
